<script setup>
import DataFilterPopup from './DataFilterPopup.vue'
import { useDataFilter } from '~/core/composables/useDataFilter'

const props = defineProps({
  simple: Boolean,
  filter: Object,
})

const { t, d } = useI18n()

const { filter } = toRefs(props)

const {
  label,
  clear,
  firstSelectedOption,
  otherSelectedOptions,
  value,
  includeLabelInSimpleValue,
} = useDataFilter(filter)

const displayDateRange = computed(() => {
  if (props.filter?.type !== 'daterange')
    return ''

  if (value?.gte === undefined && value?.lte === undefined)
    return ''
  else if (value?.gte === value?.lte)
    return `${d(value.gte, 'medium')}`
  else if (!value?.lte)
    return `&#8805; ${value?.gte ? d(value.gte, 'medium') : ''}`
  else if (!value?.gte)
    return `&#8804; ${value?.lte ? d(value.lte, 'medium') : ''}`
  else return `${d(value.gte, 'medium')} - ${d(value.lte, 'medium')}`
})

defineExpose({
  clear,
})
</script>

<template>
  <div
    v-if="firstSelectedOption"
    class="data-filter-chip h-28px max-w-full inline-flex flex-row items-center rounded-50px bg-secondary-100 px-3 py-2px ring-1 ring-secondary-200 ring-inset"
    v-bind="$attrs"
  >
    <template v-if="simple">
      <div
        class="my-1 me-2 flex flex-row items-center text-secondary-300 fw-bold"
      >
        <template v-if="filter?.type !== 'daterange'">
          <div class="truncate whitespace-nowrap text-xs">
            {{ firstSelectedOption?.label || firstSelectedOption?.name || firstSelectedOption
            }}{{ includeLabelInSimpleValue ? `, ${label}` : '' }}
          </div>
          <div
            v-if="otherSelectedOptions"
            class="ms-1 whitespace-nowrap text-xs"
          >
            {{ t('or {count} more', { count: otherSelectedOptions }) }}
          </div>
        </template>
        <div
          v-else
          class="truncate whitespace-nowrap text-xs"
          v-html="displayDateRange"
        />
      </div>
    </template>
    <template v-else>
      <span class="my-1 me-2 truncate text-xs text-secondary-300 fw-bold">{{
        label
      }}</span>

      <DataFilterPopup
        :filter="filter"
        :offset="4"
      >
        <button
          class="me-2 flex flex-row items-center rounded-35px bg-secondary-300 px-3 py-1 text-text-100 fw-semibold"
        >
          <template v-if="filter?.type !== 'daterange'">
            <div class="max-w-80px truncate whitespace-nowrap text-xs">
              {{ firstSelectedOption?.label || firstSelectedOption?.name || firstSelectedOption }}
            </div>
            <div
              v-if="otherSelectedOptions"
              class="ms-1 whitespace-nowrap text-xs"
            >
              {{ t('or {count} more', { count: otherSelectedOptions }) }}
            </div>
          </template>
          <div
            v-else
            class="truncate whitespace-nowrap text-xs"
            v-html="displayDateRange"
          />

          <div class="ms-2 flex">
            <DSIcon
              icon="i-far-chevron-down"
              class="m-2px"
              size="16"
            />
          </div>
        </button>
      </DataFilterPopup>
    </template>

    <button
      class="touch-target m-2.5px text-text-500"
      :aria-label="t('Remove Filter')"
      @click="clear"
    >
      <DSIcon
        icon="i-far-xmark-large"
        size="16"
      />
    </button>
  </div>
</template>

<i18n>
{
  "en-us": {
    "or {count} more": "or {count} more",
    "day": "0 | Day | {count} Days",
    "week": "0 | Week | {count} Weeks",
    "month": "0 | Month | {count} Months",
    "year": "0 | Year | {count} Years",
    "Past": "Past",
    "Remove Filter": "Remove Filter"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "or {count} more": "oder {count} mehr",
  "day": "0 | Tag | {count} Tage",
  "week": "0 | Woche | {count} Wochen",
  "month": "0 | Monat | {count} Monate",
  "year": "0 | Jahr | {count} Jahre",
  "Past": "Letzte",
  "Remove Filter": "Filter entfernen"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "or {count} more": "ou {count} de plus",
  "day": "0 | jour | {count} jours",
  "week": "0 | semaine | {count} semaines",
  "month": "0 | mois | {count} mois",
  "year": "0 | an | {count} ans",
  "Past": "Passé",
  "Remove Filter": "Supprimer le filtre"
}
</i18n>

<i18n locale="es" lang="json">
{
  "or {count} more": "o {count} más",
  "day": "0 | día | {count} días",
  "week": "0 | semana | {count} semanas",
  "month": "0 | mes | {count} meses",
  "year": "0 | año | {count} años",
  "Past": "Anteriores",
  "Remove Filter": "Eliminar filtro"
}
</i18n>
